// src/services/rosterBundle.js — pure roster export/import bundle logic.
//
// The interchange format is the house-idiomatic manifest/bundle shape
// (bots/*.json + marketplace/*.template.json): slugs as identity, tiers as
// roster alias slugs (fast/balanced/deep), consult wiring by bot slug, teams
// referenced by slug. No record ids, no business ids, and NEVER any provider
// credential material (llmProviderCredentials is Dexie-confidential and
// per-browser — rosters transfer, secrets do not).
//
// This module is deliberately PURE (no dataClient/pb imports) so the parse +
// plan logic is unit-testable under plain node (scripts/test-roster-transfer.mjs).
// The impure write path lives in rosterTransfer.js.

import {requireSafeContent} from './guardrails.js';

export const ROSTER_BUNDLE_KIND='command-center-roster';
export const ROSTER_BUNDLE_SCHEMA_VERSION=1;

const MAX_TEAMS=100;
const MAX_BOTS=200;

// Client tier ids (state.modelTiers / bot.modelTier) and manifest aliases →
// the PB roster-alias slugs (fast/balanced/deep) used in bundles. The reverse
// map is consumed by the write path (rosterTransfer) when hydrating the
// demo/local workspace.
export const TIER_TO_ALIAS={
  tier_fast: 'fast',
  tier_balanced: 'balanced',
  tier_deep: 'deep',
  swift: 'fast'
};
export const ALIAS_TO_CLIENT={
  fast: 'tier_fast',
  balanced: 'tier_balanced',
  deep: 'tier_deep',
  swift: 'tier_fast'
};
const AGENT_CLASSES=['chat_simple','tooling_advanced'];

// Accept tier expressed as a bundle alias (fast/balanced/deep — what export
// emits), a client id (tier_deep — hydrated state shapes), or the manifest
// alias 'swift'; anything else falls back to balanced.
function tierAlias(value) {
  const raw=String(value || '');
  if (raw==='fast' || raw==='balanced' || raw==='deep') return raw;
  return TIER_TO_ALIAS[raw] || 'balanced';
}

export function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .slice(0,48);
}

function bundleError(code,message) {
  const error=new Error(message);
  error.code=code;
  return error;
}

// ── Export ───────────────────────────────────────────────────────────────────

/**
 * Build a transfer bundle from a workspace snapshot (dataClient.peek() shape).
 * Everything identity-shaped is preserved (names, roles, prompts, tiers,
 * lead/consult wiring, enabled flags, team membership); everything runtime or
 * confidential is stripped (ids, colors, BYO provider assignments).
 */
export function buildRosterBundle(state) {
  const teams=(state.teams || []).map((team)=> ({
    slug: slugify(team.slug || team.name),
    name: team.name,
    module: team.module || 'headquarters',
    enabled: team.enabled !== false
  }));

  const teamSlugById=new Map(
    (state.teams || []).map((team)=> [team.id,slugify(team.slug || team.name)])
  );
  const botSlugById=new Map(
    (state.bots || []).map((bot)=> [bot.id,slugify(bot.slug || bot.name)])
  );

  const bots=(state.bots || []).map((bot)=> ({
    slug: slugify(bot.slug || bot.name),
    name: bot.name,
    role: bot.role || 'Specialist',
    system_prompt: bot.systemPrompt || '',
    team: bot.teamId ? (teamSlugById.get(bot.teamId) || null) : null,
    is_lead: Boolean(bot.isLead),
    enabled: bot.enabled !== false,
    model_tier: TIER_TO_ALIAS[bot.modelTier] || 'balanced',
    agent_class: AGENT_CLASSES.includes(bot.agentClass) ? bot.agentClass : 'chat_simple',
    tools: Array.isArray(bot.tools) ? bot.tools.filter((tool)=> typeof tool==='string') : [],
    consultable_bots: (Array.isArray(bot.consultableBots) ? bot.consultableBots : [])
      .map((id)=> botSlugById.get(id))
      .filter(Boolean)
      .filter((slug)=> slug!==slugify(bot.slug || bot.name))
  }));

  return {
    kind: ROSTER_BUNDLE_KIND,
    schema_version: ROSTER_BUNDLE_SCHEMA_VERSION,
    exported_at: new Date().toISOString(),
    source: {
      name: state.business?.name || 'A business'
    },
    teams,
    bots
  };
}

// ── Parse + validate ─────────────────────────────────────────────────────────

/**
 * Parse and normalize an incoming bundle (object or JSON string).
 * Throws Error with a .code for every rejection — never partially accepts.
 */
export function parseRosterBundle(input) {
  let bundle=input;

  if (typeof input==='string') {
    try {
      bundle=JSON.parse(input);
    } catch {
      throw bundleError('ROSTER_BUNDLE_INVALID_JSON','The file is not valid JSON.');
    }
  }

  if (!bundle || typeof bundle!=='object' || Array.isArray(bundle)) {
    throw bundleError('ROSTER_BUNDLE_INVALID','The file is not a roster bundle object.');
  }
  if (bundle.kind!==ROSTER_BUNDLE_KIND) {
    throw bundleError('ROSTER_BUNDLE_WRONG_KIND','This file is not a Command Center roster bundle (missing kind marker).');
  }
  if (bundle.schema_version!==ROSTER_BUNDLE_SCHEMA_VERSION) {
    throw bundleError(
      'ROSTER_BUNDLE_UNSUPPORTED_VERSION',
      `Unsupported roster bundle schema_version "${bundle.schema_version}" — expected ${ROSTER_BUNDLE_SCHEMA_VERSION}.`
    );
  }

  const rawTeams=Array.isArray(bundle.teams) ? bundle.teams : [];
  const rawBots=Array.isArray(bundle.bots) ? bundle.bots : [];

  if (!rawTeams.length && !rawBots.length) {
    throw bundleError('ROSTER_BUNDLE_EMPTY','The roster bundle contains no teams and no bots.');
  }
  if (rawTeams.length > MAX_TEAMS) {
    throw bundleError('ROSTER_BUNDLE_TOO_LARGE',`The bundle declares ${rawTeams.length} teams (max ${MAX_TEAMS}).`);
  }
  if (rawBots.length > MAX_BOTS) {
    throw bundleError('ROSTER_BUNDLE_TOO_LARGE',`The bundle declares ${rawBots.length} bots (max ${MAX_BOTS}).`);
  }

  const notes=[];
  const teams=[];
  const seenTeamSlugs=new Set();

  rawTeams.forEach((team,index)=> {
    if (!team || typeof team!=='object') {
      throw bundleError('ROSTER_BUNDLE_INVALID',`teams[${index}] is not an object.`);
    }
    const slug=slugify(team.slug || team.name);
    if (!slug) {
      throw bundleError('ROSTER_BUNDLE_INVALID',`teams[${index}] has neither a usable slug nor a name.`);
    }
    if (seenTeamSlugs.has(slug)) {
      throw bundleError('ROSTER_BUNDLE_INVALID',`Duplicate team slug "${slug}" in the bundle.`);
    }
    seenTeamSlugs.add(slug);
    teams.push({
      slug,
      name: String(team.name || slug),
      module: String(team.module || 'headquarters'),
      enabled: team.enabled !== false
    });
  });

  const bots=[];
  const seenBotSlugs=new Set();

  rawBots.forEach((bot,index)=> {
    if (!bot || typeof bot!=='object') {
      throw bundleError('ROSTER_BUNDLE_INVALID',`bots[${index}] is not an object.`);
    }
    const name=String(bot.name || '').trim();
    const slug=slugify(bot.slug || name);
    if (!name) {
      throw bundleError('ROSTER_BUNDLE_INVALID',`bots[${index}] (${bot.slug || 'no slug'}) has no name.`);
    }
    if (!slug) {
      throw bundleError('ROSTER_BUNDLE_INVALID',`bots[${index}] ("${name}") has no usable slug.`);
    }
    if (seenBotSlugs.has(slug)) {
      throw bundleError('ROSTER_BUNDLE_INVALID',`Duplicate bot slug "${slug}" in the bundle.`);
    }
    seenBotSlugs.add(slug);

    const role=String(bot.role || '').trim();
    if (!role) notes.push(`Bot "${name}" has no role — importing with role "Specialist".`);
    const systemPrompt=String(bot.system_prompt ?? bot.systemPrompt ?? '').trim();
    if (!systemPrompt) notes.push(`Bot "${name}" has no system prompt — importing with a generic prompt.`);

    // Guardrails: an imported prompt is untrusted input that becomes a live
    // system prompt — run the same safety gate every other write path uses.
    requireSafeContent(systemPrompt || `Specialist for a business.`, 'ROSTER_BUNDLE_UNSAFE');

    const team=bot.team===null || bot.team===undefined || bot.team==='' ? null : slugify(bot.team);

    bots.push({
      slug,
      name,
      role: role || 'Specialist',
      system_prompt: systemPrompt || 'A focused specialist supporting the business.',
      team,
      is_lead: Boolean(bot.is_lead ?? bot.isLead),
      enabled: bot.enabled !== false,
      model_tier: tierAlias(bot.model_tier ?? bot.modelTier),
      agent_class: AGENT_CLASSES.includes(bot.agent_class) ? bot.agent_class : 'chat_simple',
      tools: Array.isArray(bot.tools) ? bot.tools.filter((tool)=> typeof tool==='string') : [],
      consultable_bots: (Array.isArray(bot.consultable_bots) ? bot.consultable_bots : [])
        .map((ref)=> slugify(ref))
        .filter(Boolean)
        .filter((ref)=> ref!==slug)
    });
  });

  // Bundle-internal one-Lead invariant (build.md §7.2), checkable at parse
  // time: group imported bots by team slug and require exactly one Lead for
  // every multi-bot group. (A bot may also reference a team that only exists
  // in the LIVE workspace — that case is planned, not parsed, and any live
  // Lead collision demotes the incoming bot instead of rejecting the bundle.)
  const byTeam=new Map();
  for (const bot of bots) {
    if (!bot.team) continue;
    byTeam.set(bot.team,[...(byTeam.get(bot.team) || []),bot]);
  }
  for (const [teamSlug,teamBots] of byTeam) {
    if (teamBots.length < 2) continue;
    const leads=teamBots.filter((bot)=> bot.is_lead);
    if (leads.length!==1) {
      throw bundleError(
        'ROSTER_BUNDLE_LEAD_CONFLICT',
        `Bundle team "${teamSlug}" has ${leads.length} Leads across ${teamBots.length} bots — a multi-bot team must have exactly one Lead (build.md §7.2).`
      );
    }
  }

  return {teams,bots,notes};
}

// ── Import planning ──────────────────────────────────────────────────────────

/**
 * Plan an import against the CURRENT workspace state. Pure — used by both the
 * preview UI and the writer so preview and apply can never drift.
 *
 * Merge semantics (owner requirement): ADD new bots/teams; NEVER overwrite,
 * rename, or disable existing live records. A slug that already exists live
 * (team or bot) is skipped and reported. The only adjustments ever made are
 * to the INCOMING records: a Lead joining a live team that already has a
 * Lead is demoted to specialist (noted), and consult references that resolve
 * to no bot (bundle ∪ live) are dropped (noted).
 */
export function planRosterImport(bundle,state) {
  const liveTeams=state.teams || [];
  const liveBots=state.bots || [];
  const liveTeamSlugs=new Set(liveTeams.map((team)=> slugify(team.slug || team.name)));
  const liveBotSlugs=new Set(liveBots.map((bot)=> slugify(bot.slug || bot.name)).filter(Boolean));
  const liveBotBySlug=new Map(liveBots.map((bot)=> [slugify(bot.slug || bot.name),bot]));
  const liveTeamBySlug=new Map(liveTeams.map((team)=> [slugify(team.slug || team.name),team]));
  const moduleSlugs=new Set((state.modules || []).map((module)=> module.slug));

  const skippedTeams=[];
  const skippedBots=[];
  const notes=[...(bundle.notes || [])];

  // 1. Teams: new slugs only; existing slugs are never touched.
  const newTeams=[];
  for (const team of bundle.teams) {
    if (liveTeamSlugs.has(team.slug)) {
      skippedTeams.push({slug: team.slug,name: team.name,reason: 'already exists'});
      continue;
    }
    if (!moduleSlugs.has(team.module)) {
      notes.push(`Team "${team.name}" module "${team.module}" is not installed — importing under Headquarters.`);
    }
    newTeams.push({
      ...team,
      module: moduleSlugs.has(team.module) ? team.module : 'headquarters'
    });
  }

  // 2. Bots: new slugs only. Lead collisions with a live team demote the
  // INCOMING bot (the live Lead is untouchable by design).
  const newBots=[];
  for (const bot of bundle.bots) {
    if (liveBotSlugs.has(bot.slug)) {
      const live=liveBotBySlug.get(bot.slug);
      skippedBots.push({slug: bot.slug,name: bot.name,reason: `already exists (${live?.name || 'live bot'})`});
      continue;
    }

    let teamSlug=bot.team;
    let joinsExistingTeam=false;

    if (teamSlug) {
      if (liveTeamSlugs.has(teamSlug)) {
        joinsExistingTeam=true;
      } else if (!bundle.teams.some((team)=> team.slug===teamSlug)) {
        // References neither a bundle team nor a live team — import as a lone
        // specialist rather than dropping the bot entirely.
        notes.push(`Bot "${bot.name}" references team "${teamSlug}" which is neither in the bundle nor live — importing as a lone specialist.`);
        teamSlug=null;
      }
      // Else: the team is being created in this same import — fine.
    }

    let isLead=bot.is_lead;

    if (isLead && joinsExistingTeam) {
      const liveTeam=liveTeamBySlug.get(teamSlug);
      const existingLead=liveBots.find(
        (item)=> liveTeam && item.teamId===liveTeam.id && item.isLead
      );
      if (existingLead) {
        isLead=false;
        notes.push(
          `Bot "${bot.name}" is a Lead in the bundle, but live team "${liveTeam?.name || teamSlug}" already has a Lead (${existingLead.name}) — importing as a specialist.`
        );
      }
    }

    newBots.push({...bot,team: teamSlug,is_lead: isLead});
  }

  // 3. Consult wiring: resolve refs against imported ∪ live bot slugs.
  const resolvableSlugs=new Set([...liveBotSlugs,...newBots.map((bot)=> bot.slug)]);
  const wiredBots=newBots.map((bot)=> {
    const kept=[];
    for (const ref of bot.consultable_bots) {
      if (resolvableSlugs.has(ref)) kept.push(ref);
      else notes.push(`Bot "${bot.name}": consultable reference "${ref}" resolves to no imported or live bot — dropped.`);
    }
    return {...bot,consultable_bots: kept};
  });

  return {
    newTeams,
    newBots: wiredBots,
    skippedTeams,
    skippedBots,
    notes
  };
}
